import React, { useState } from "react";
import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import MemberReportFilters from "../../../MemberReports/components/MemberReportFilters/MemberReportFilters";
import { useMemberReport } from "../../../MemberReports/hooks/useMemberReport";
import { useDownloadMemberReportCsv } from "../../../MemberReports/hooks/useDownloadMemberReportCsv";
import { defaultDateRange } from "../../../MemberReports/utils/defaultDateRange";
import { fetchMyVouchers } from "../../api/fetchMyVouchers/fetchMyVouchersApi";
import { fetchMyVouchersCsv } from "../../api/fetchMyVouchers/fetchMyVouchersCsvApi";
import {
  MyVoucherItem,
  MyVouchersSortKey,
} from "../../api/fetchMyVouchers/types";
import { myVouchersColumns } from "../MyVouchersColumns/MyVouchersColumns";

const columns = myVouchersColumns();

const SORT_KEYS: readonly MyVouchersSortKey[] = [
  "voucherCode",
  "dateIssued",
  "expirationDate",
  "totalSeats",
];

/**
 * Member-facing voucher report. Authenticated only: the backend scopes the
 * rows to the active company, so there is no permission and no guard here.
 */
const MyVouchersPage: React.FC = () => {
  const [defaultFilters] = useState(defaultDateRange);

  const {
    items,
    totalCount,
    loading,
    pagination,
    sorting,
    filters,
    exportParams,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useMemberReport<MyVoucherItem, MyVouchersSortKey>({
    fetchPage: fetchMyVouchers,
    sortKeys: SORT_KEYS,
    initialFilters: defaultFilters,
  });

  const { loading: csvLoading, downloadCsv } = useDownloadMemberReportCsv({
    fetchCsv: fetchMyVouchersCsv,
    fileNamePrefix: "my-vouchers",
  });

  return (
    <MainPageWrapper subtitle="Your company's event vouchers" title="Vouchers">
      <MemberReportFilters
        dateLabel="Date Issued"
        defaultFilters={defaultFilters}
        filters={filters}
        isExporting={csvLoading}
        onExportCsv={() => downloadCsv(exportParams)}
        onFilterChange={handleFilterChange}
      />
      <DataTable<MyVoucherItem>
        columns={columns}
        data={items}
        loading={loading}
        noDataMessage="No vouchers found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(row) => row.voucherCode}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default MyVouchersPage;
